import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import type { NavItem } from "@memorang/ui/components/Navigation/types";
import MemorangIcons from "components/MemorangIcons";
import type { IconType } from "components/types/icon";
import type { Href } from "expo-router";

export type NavigationRoute = {
	name: string;
	enabled: boolean;
	custom?: boolean;
	icon: { default: string; selected: string };
};

export type DrawerNavResult = {
	items: NavItem[];
	routeMap: Record<string, Href>;
};

export type BuildParams = {
	navigationRoutes: NavigationRoute[];
	showStudyPackAsHome: boolean;
	studyPackCollectionId?: number;
	showStudyPackViews: boolean;
	practiceTestProductNumericId?: number;
	practiceTestEnabled?: { web: boolean; mobile: boolean };
	practiceTestLabel?: string;
	storeEnabled: boolean;
	showRedeem?: boolean;
	t: (key: string) => string;
	isWebPlatform: boolean;
};

const makeIcon =
	(name: string, custom?: boolean) =>
	(props: { size: number; color: string }) =>
		custom ? (
			<MemorangIcons name={name} size={props.size} color={props.color} />
		) : (
			<Icon name={name as IconType} size={props.size} color={props.color} />
		);

export const buildDrawerNavItems = ({
	navigationRoutes,
	showStudyPackAsHome,
	studyPackCollectionId,
	showStudyPackViews,
	practiceTestProductNumericId,
	practiceTestEnabled,
	practiceTestLabel,
	storeEnabled,
	showRedeem,
	t,
	isWebPlatform,
}: BuildParams): DrawerNavResult => {
	const items: NavItem[] = [];
	const routeMap: Record<string, Href> = {};

	const canShowStudyPack =
		showStudyPackViews &&
		!showStudyPackAsHome &&
		studyPackCollectionId !== 38 &&
		studyPackCollectionId !== 20;

	const filteredRoutes = navigationRoutes.filter((item) => {
		if (item.name === "profile") {
			return !isWebPlatform;
		}

		if (item.name === "study-pack") {
			return !isWebPlatform && canShowStudyPack && item.enabled;
		}

		return item.enabled;
	});

	for (const item of filteredRoutes) {
		const key = item.name;
		items.push({
			key,
			label: t(`navbar.${key}`),
			icon: makeIcon(item.icon.default, item.custom),
			activeIcon: makeIcon(item.icon.selected, item.custom),
		});
		routeMap[key] = `/${key}` as Href;
	}

	const showPracticeTests =
		practiceTestProductNumericId &&
		(isWebPlatform ? practiceTestEnabled?.web : practiceTestEnabled?.mobile);

	if (showPracticeTests) {
		items.splice(1, 0, {
			key: "practice-tests",
			label: practiceTestLabel ?? "Practice Tests",
			icon: makeIcon("clipboard-check-outline"),
			activeIcon: makeIcon("clipboard-check"),
		});
		routeMap["practice-tests"] = "/practice-tests" as Href;
	}

	if (isWebPlatform) {
		if (canShowStudyPack) {
			items.splice(showPracticeTests ? 2 : 1, 0, {
				key: "study-pack",
				label: "Study-Pack",
				icon: makeIcon("study-pack-outline", true),
				activeIcon: makeIcon("study-pack", true),
			});
			routeMap["study-pack"] = "/study-pack" as Href;
		}

		if (storeEnabled || showRedeem) {
			items.push({
				key: "redeem",
				label: t("navbar.redeem"),
				icon: makeIcon("gift-outline"),
				activeIcon: makeIcon("gift"),
			});
			routeMap.redeem = "/redeem?step=redeem" as Href;
		}
	}

	return { items, routeMap };
};
